import { useTranslation } from "react-i18next";
import { ReactComponent as Download } from "../../assets/svg/download.svg";
import { ReactComponent as CopyIcon } from "../../assets/svg/copy.svg";
import { COLORS } from "../../constants/design/colors";
import { BaseModal } from "../BaseModal";
import { Spinner } from "../shared/Spinner";
import {
  BgDot,
  CancelButton,
  CardPreviewWrapper,
  ControlButtonRow,
  ControlGroup,
  ControlIconBtn,
  ControlsSection,
  EnlargedPnLCardContainer,
  EnlargedPnLCardWrapper,
  IconButton,
  ShareActionsRow,
  ShareHeaderActions,
  ShareModalHeader,
  TwitterButton,
} from "./style";
import { PnLCardInner } from "./PnLCardInner";
import { LandscapeIcon, PortraitIcon, TrendingIcon } from "./icons";
import { usePnLCardModal } from "./usePnLCardModal";
import { IPnLCardProps } from "./types";

// ---------------------------------------------------------------------------
// Main modal component
// ---------------------------------------------------------------------------

export function PnLCardModal(props: IPnLCardProps) {
  const { pnlType, pnlData, onHideModal, show } = props;
  const { t } = useTranslation("app", { keyPrefix: "PnLCardModal" });

  const {
    orientation,
    setOrientation,
    bgMode,
    setBgMode,
    downloading,
    copying,
    exportRef,
    pnlContent,
    cardTheme,
    handleDownload,
    handleCopy,
    handleTwitterShare,
  } = usePnLCardModal(props);

  return (
    <EnlargedPnLCardContainer>
      {/* Hidden export target – always rendered, captures current theme */}
      <EnlargedPnLCardWrapper>
        <div ref={exportRef}>
          <PnLCardInner
            pnlType={pnlType}
            pnlData={pnlData}
            pnlContent={pnlContent}
            theme={cardTheme}
          />
        </div>
      </EnlargedPnLCardWrapper>

      <BaseModal
        onHide={onHideModal}
        show={show}
        noContentPadding
        showHeader={false}
      >
        {/* Custom modal header */}
        <ShareModalHeader>
          <span>{t("pnl_share", { defaultValue: "PnL Share" })}</span>
          <ShareHeaderActions>
            <IconButton
              onClick={handleDownload}
              disabled={downloading}
              title="Download"
            >
              {downloading ? <Spinner /> : <Download />}
            </IconButton>
            <IconButton
              onClick={handleCopy}
              disabled={copying}
              title="Copy to clipboard"
            >
              {copying ? <Spinner /> : <CopyIcon />}
            </IconButton>
          </ShareHeaderActions>
        </ShareModalHeader>

        {/* Card preview */}
        <CardPreviewWrapper>
          <PnLCardInner
            pnlType={pnlType}
            pnlData={pnlData}
            pnlContent={pnlContent}
            theme={cardTheme}
          />
        </CardPreviewWrapper>

        {/* VIEW + BACKGROUND controls */}
        <ControlsSection>
          <ControlGroup>
            <label>{t("view", { defaultValue: "VIEW" })}</label>
            <ControlButtonRow>
              <ControlIconBtn
                active={orientation === "landscape"}
                onClick={() => setOrientation("landscape")}
                title="Landscape"
              >
                <LandscapeIcon />
              </ControlIconBtn>
              <ControlIconBtn
                active={orientation === "portrait"}
                onClick={() => setOrientation("portrait")}
                title="Portrait"
              >
                <PortraitIcon />
              </ControlIconBtn>
            </ControlButtonRow>
          </ControlGroup>

          <ControlGroup>
            <label>{t("background", { defaultValue: "BACKGROUND" })}</label>
            <ControlButtonRow>
              <ControlIconBtn
                active={bgMode === "dark"}
                onClick={() => setBgMode("dark")}
                title="Dark"
              >
                <BgDot $color="#111" />
              </ControlIconBtn>
              <ControlIconBtn
                active={bgMode === "chart"}
                onClick={() => setBgMode("chart")}
                title="Chart"
              >
                <TrendingIcon />
              </ControlIconBtn>
              <ControlIconBtn
                active={bgMode === "gradient"}
                onClick={() => setBgMode("gradient")}
                title="Gradient"
              >
                <BgDot
                  $color={
                    cardTheme.isPositive
                      ? COLORS.up.three
                      : COLORS.down.three
                  }
                />
              </ControlIconBtn>
            </ControlButtonRow>
          </ControlGroup>
        </ControlsSection>

        {/* Action buttons */}
        <ShareActionsRow>
          <CancelButton onClick={onHideModal}>
            {t("cancel", { defaultValue: "Cancel" })}
          </CancelButton>
          <TwitterButton onClick={handleTwitterShare}>
            {t("post_to_x", { defaultValue: "Post to X (Twitter)" })}
          </TwitterButton>
        </ShareActionsRow>
      </BaseModal>
    </EnlargedPnLCardContainer>
  );
}
